import React, { useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import withSidebar from './components/SideBar/WithSidebar';
import Billing from 'modules/cases/Billing';
import ActivityLog from 'modules/dashboard/components/ActivityLog/ActivityLog';
import Notes from 'modules/cases/Note/Notes';
import TextMessages from 'modules/messages/TextMessages/TextMessages';
import Overview from './components/Overview/Overview';
import TaskBoard from 'modules/TasksV2';

import { useLeadDetails } from 'modules/lead/hooks/useLeadDetails';
import NewFileManager from 'modules/file_manager/NewFileManager';
import { useUserDetailsContext } from 'context/userDetailsContext';
import MessageRouterLead from 'modules/messages/SecureMessages/MessageRouterLead';
import { Information } from 'modules/cases/Information/Information';
import Contacts from 'modules/cases/CaseDetails/Contacts/Contacts';
import NoteDetails from 'modules/cases/NoteDetails';
import Intake from 'modules/cases/CaseDetails/Intake/Intake';
import MailBox from 'modules/mailbox/MailBox';
import { setAppTitle } from 'utils/utils';
import { generateFullName } from 'utils/generateFullNameUtils';
import Automation from 'modules/cases/CaseDetails/automation/Automation';
import CalendarV2 from 'modules/calendarv2';

function LeadDetails({ match }) {
  const { leadDetails, loadLeadDetails } = useLeadDetails();
  const { checkSubPermission } = useUserDetailsContext();

  useEffect(() => {
    setAppTitle('Leads');
    if (leadDetails?.client_details?.[0]) {
      const clientName = generateFullName(leadDetails?.client_details?.[0]);
      setAppTitle('Leads', clientName);
    }

    return () => setAppTitle();
  }, [leadDetails]);

  return (
    <>
      {Boolean(leadDetails) ? (
        <Switch>
          <Route
            exact
            path={`${match.path}/overview`}
            render={(props) => <Overview {...props} leadDetails={leadDetails} loadLeadDetails={loadLeadDetails} />}
          />
          <Route
            exact
            path={`${match.path}`}
            render={(props) => (
              <Information
                {...props}
                caseDetails={leadDetails}
                handleGetCaseAction={loadLeadDetails}
                fetchLeadDetails={loadLeadDetails}
                isLeadSpecific={true}
              />
            )}
          />
          <Route
            path={`${match.path}/files`}
            render={(props) => (
              <NewFileManager caseDetails={leadDetails} isLeadFileManager={true} isClosed={leadDetails?.is_lead && !leadDetails?.is_open} />
            )}
          />
          <Route
            path={`${match.path}/contacts`}
            render={(props) => (
              <Contacts
                {...props}
                caseDetails={leadDetails}
                isLeadSpecific={true}
                getDetails={loadLeadDetails}
                isLeadClosed={leadDetails?.is_lead && !leadDetails?.is_open}
              />
            )}
          />
          <Route
            path={`${match.path}/calendar`}
            render={(props) => <CalendarV2 {...props} caseDetails={leadDetails} isFromLead={true} getCaseDetails={loadLeadDetails} />}
          />
          {checkSubPermission('task_management') && (
            <Route
              path={`${match.path}/task`}
              render={(props) => (
                <TaskBoard
                  {...props}
                  caseDetails={leadDetails}
                  isFromLead={true}
                  isLeadClosed={leadDetails?.is_lead && !leadDetails?.is_open}
                  getCaseDetails={loadLeadDetails}
                />
              )}
            />
          )}
          {checkSubPermission('email_service') && (
            <Route path={`${match.path}/mails`} render={(props) => <MailBox {...props} caseDetails={leadDetails} />} />
          )}
          <Route
            path={`${match.path}/messages`}
            render={(props) => (
              <MessageRouterLead
                {...props}
                caseDetails={leadDetails}
                isFromCaseSpecificModule={false}
                isLeadSpecific={true}
                isLeadClosed={leadDetails.is_lead && !leadDetails.is_open}
                getCaseDetails={loadLeadDetails}
              />
            )}
          />
          {checkSubPermission('texting') && (
            <Route
              path={`${match.path}/text-message`}
              render={(props) => <TextMessages {...props} leadDetails={leadDetails} isFromLead={true} />}
            />
          )}
          <Route
            path={`${match.path}/note`}
            render={(props) => (
              <Notes
                {...props}
                leadDetails={leadDetails}
                isFromCaseSpecificModule
                isLeadSpecific
                isLeadClosed={leadDetails.is_lead && !leadDetails.is_open}
                getCaseDetails={loadLeadDetails}
              />
            )}
          />
          <Route
            path={`${match.path}/notes/:id`}
            render={(props) => (
              <NoteDetails
                {...props}
                caseDetails={leadDetails}
                isFromCaseSpecificModule
                isLeadSpecific
                isLeadClosed={leadDetails.is_lead && !leadDetails.is_open}
                getCaseDetails={loadLeadDetails}
              />
            )}
          />
          <Route
            path={`${match.path}/billing`}
            render={(props) => (
              <Billing
                {...props}
                ActivityLog
                caseDetails={leadDetails}
                isLeadSpecific
                isLeadClosed={leadDetails.is_lead && !leadDetails.is_open}
                getCaseDetails={loadLeadDetails}
              />
            )}
          />
          <Route path={`${match.path}/intake`} render={(props) => <Intake {...props} caseDetails={leadDetails} isLead />} />
          <Route
            path={`${match.path}/activity`}
            render={(props) => <ActivityLog {...props} caseDetails={leadDetails} isCaseSpecific={true} isLead={true} />}
          />
          <Route exact path={`${match.path}/automation`} render={(props) => <Automation {...props} caseDetails={leadDetails} isLead />} />
        </Switch>
      ) : (
        <h2 className="title gray-800 mb-0 fs-3 fs-md-1 case-title">Loading</h2>
      )}
    </>
  );
}

export default withSidebar(LeadDetails);
